/**
 * Copyright (c) Simam Digital. Built on Project Flowerbed
 * (Copyright (c) Meta Platforms, Inc. and affiliates).
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import * as localForage from 'localforage';

import {
	PlayerWalletComponent,
	RegionComponent,
} from '../../components/EconomyComponents';
import { REGION_SIGNATURE, getSeedInfo } from '../../SeedCatalog';

import { System } from 'ecsy';
import { getOnlyEntity } from '../../utils/entityUtils';

const STARTING_COINS = 50;
const WALLET_STORAGE_KEY = 'garden_wallet';
const SAVE_INTERVAL = 3; // seconds between debounced coin saves

/**
 * Owns the player's wallet singleton and their region ("Living World" seed
 * flavour), and persists the coin balance to localForage so it survives
 * reloads. Other systems (planting, picking, HUD) read/mutate the wallet
 * through the {@link GardenEconomySystem.getWallet} helper.
 */
export class GardenEconomySystem extends System {
	init() {
		this._saveTimer = 0;

		const entity = this.world.createEntity();
		entity.addComponent(PlayerWalletComponent, { coins: STARTING_COINS });
		entity.addComponent(RegionComponent);
		this._walletEntity = entity;

		this._guessRegion();
		this._loadWallet();
	}

	async _loadWallet() {
		try {
			const saved = await localForage.getItem(WALLET_STORAGE_KEY);
			if (saved && typeof saved.coins === 'number') {
				const wallet = this._walletEntity.getMutableComponent(
					PlayerWalletComponent,
				);
				wallet.coins = saved.coins;
				wallet.totalEarned = saved.totalEarned || 0;
			}
		} catch (e) {
			console.warn('Failed to load wallet', e);
		}
	}

	_saveWallet() {
		const wallet = this._walletEntity.getComponent(PlayerWalletComponent);
		localForage
			.setItem(WALLET_STORAGE_KEY, {
				coins: wallet.coins,
				totalEarned: wallet.totalEarned,
			})
			.catch((e) => console.warn('Failed to save wallet', e));
	}

	_guessRegion() {
		let region = '';
		try {
			const locale =
				(navigator.languages && navigator.languages[0]) ||
				navigator.language ||
				'';
			const parts = locale.split('-');
			region = (parts[1] || '').toUpperCase();
		} catch (e) {
			region = '';
		}

		const signatureFamily = REGION_SIGNATURE[region] || '';
		const regionComponent = this._walletEntity.getMutableComponent(
			RegionComponent,
		);
		regionComponent.region = region;
		regionComponent.signatureSeed = signatureFamily;

		if (signatureFamily) {
			console.log(
				`Living World: region ${region} — signature seed ${
					getSeedInfo(signatureFamily).displayName
				}`,
			);
		}
	}

	execute(delta) {
		const walletEntity = getOnlyEntity(this.queries.wallet, false);
		if (!walletEntity) return;
		const wallet = walletEntity.getMutableComponent(PlayerWalletComponent);

		// Debounced persistence: save at most every SAVE_INTERVAL seconds after
		// the balance last changed.
		if (wallet.dirty) {
			this._saveTimer += delta;
			if (this._saveTimer >= SAVE_INTERVAL) {
				wallet.dirty = false;
				this._saveTimer = 0;
				this._saveWallet();
			}
		}
	}
}

GardenEconomySystem.queries = {
	wallet: { components: [PlayerWalletComponent] },
};
